import { useState, useEffect } from 'react';
import { Box, Icon, Link, Text } from '@nimbus-ds/components';
import { CheckCircleIcon, ChevronLeftIcon } from '@nimbus-ds/icons';
import { SideModal } from '@nimbus-ds/patterns';
import { CancelAndConfirmButtons, ErrorScreen } from 'commons/components';
import { CitiesInterface } from 'domains/Shipping/Addresses/addressesSlice/types';
import useGetCities from 'domains/Shipping/Addresses/hooks/useGetCities';
import { CityInterface } from 'domains/Shipping/components/shared/ZoneManagement/DeliveryZoneCard/DeliveryZoneCard';
import ModalFooter from 'domains/Shipping/DeliveryMethods/pages/components/SelectionModals/shared/ModalFooter';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import CitiesList from './CitiesList';
import { getAllCitiesChecked } from './utils';

interface SelectCitiesModalProps {
  isOpen: boolean;
  onChange: (citiesSelected: CityInterface[], allChecked: boolean) => void;
  onClose: () => void;
  selectedCities: string[];
  hasAllCities: boolean;
  selectedNameProvince: string;
  selectedProvinceCode: string;
}

function SelectCitiesModal({
  isOpen,
  onClose,
  onChange,
  selectedCities,
  hasAllCities,
  selectedProvinceCode,
  selectedNameProvince,
}: SelectCitiesModalProps) {
  const t = useTranslationShipping();

  const { cities, isError, isLoading, fetchCities } =
    useGetCities(selectedProvinceCode);

  const [newSelectedCities, setNewSelectedCities] = useState(selectedCities);
  const [filter, setFilter] = useState<CitiesInterface[]>(cities);
  const [allChecked, setAllChecked] = useState<boolean | 'indeterminate'>(
    hasAllCities || getAllCitiesChecked(selectedCities, cities),
  );

  const handleSetNewSelectedCities = (newSelectedCities: string[]) => {
    setNewSelectedCities(newSelectedCities);
  };

  const handleSetFilter = (filter: CitiesInterface[]) => {
    setFilter(filter);
  };

  const handleSelectAll = (allChecked: boolean | 'indeterminate') => {
    setAllChecked(allChecked);
  };

  const handleSave = () => {
    const selectedCitiesObjects = cities
      .filter((city) => newSelectedCities.includes(city.id))
      .map((city) => ({ id: city.id, name: city.name }));

    onChange(
      selectedCitiesObjects,
      allChecked !== 'indeterminate' ? allChecked : false,
    );
    onClose();
  };

  useEffect(() => {
    if (cities.length === 0) {
      fetchCities();
    }

    if (cities.length > 0) {
      getAllCitiesChecked(selectedCities, cities);
      setFilter(cities);
    }

    if (hasAllCities && cities.length > 0) {
      setNewSelectedCities(cities.map((city) => city.id));
    }
  }, [cities, fetchCities, hasAllCities, selectedCities]);

  return (
    <SideModal
      open={isOpen}
      onRemove={onClose}
      maxWidth={{ xs: '100%', md: '45%', lg: '540px' }}
      paddingBody="none"
      headerAction={
        <Link onClick={onClose} textDecoration="none">
          <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
          <Text fontWeight="bold" fontSize="highlight">
            {t('deliveryMethods.customShipping.selectCitiesModal.headerTitle')}
          </Text>
        </Link>
      }
      headerIcon={
        <Link onClick={handleSave} textDecoration="none">
          <Icon source={<CheckCircleIcon />} />
        </Link>
      }
    >
      {isError && (
        <ErrorScreen
          description={t('deliveryMethods.customShipping.error.title')}
          onRetry={fetchCities}
        />
      )}
      {!isError && isLoading && !cities.length && <CitiesList.Skeleton />}
      {!isError && !isLoading && !!cities.length && (
        <Box
          display="flex"
          flexDirection="column"
          height="calc(100vh - 85px)"
          overflow="hidden"
        >
          <Box flex="1" overflow="auto" overflowX="hidden">
            <CitiesList
              cities={cities}
              filteredCities={filter}
              newSelectedCities={newSelectedCities}
              allChecked={allChecked}
              hasAllCities={hasAllCities}
              provinceName={selectedNameProvince}
              handleSelectAll={handleSelectAll}
              handleSetFilter={handleSetFilter}
              handleSetNewSelectedCities={handleSetNewSelectedCities}
            />
          </Box>
          {filter.length > 0 && (
            <ModalFooter>
              <CancelAndConfirmButtons
                confirmText={t(
                  'deliveryMethods.customShipping.selectCitiesModal.save',
                )}
                cancelText={t(
                  'deliveryMethods.customShipping.selectCitiesModal.cancel',
                )}
                onConfirm={handleSave}
                onCancel={onClose}
              />
            </ModalFooter>
          )}
        </Box>
      )}
    </SideModal>
  );
}

export default SelectCitiesModal;
